import React, { useEffect, useRef, useState } from 'react';
import { HashIcon, SendIcon, ZapIcon } from 'lucide-react';
import { slackChannels, type SlackMessage } from '../data/slackChannels';
type SlackChannelPanelProps = {
  loanId: string;
  loanNumber: string;
};
export function SlackChannelPanel({
  loanId,
  loanNumber
}: SlackChannelPanelProps) {
  const channel = slackChannels[loanId];
  const [messages, setMessages] = useState<SlackMessage[]>(channel?.messages ?? []);
  const [draft, setDraft] = useState('');
  const [justPosted, setJustPosted] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMessages(slackChannels[loanId]?.messages ?? []);
    setDraft('');
  }, [loanId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest'
    });
  }, [messages.length]);
  useEffect(() => {
    if (!justPosted) return;
    const timer = window.setTimeout(() => setJustPosted(false), 2500);
    return () => window.clearTimeout(timer);
  }, [justPosted]);
  if (!channel) {
    return <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center" data-id="element-647" data-mp="3qnqjklta9uyx">
        <p className="text-[13px] font-medium text-ink" data-id="element-648" data-mp="3lpp388n8s9r5">No Slack channel yet</p>
        <p className="mt-1 text-[12.5px] text-gray-500" data-id="element-649" data-mp="3b6dsqizso5z">
          A channel is created automatically when {loanNumber} moves into processing.
        </p>
      </div>;
  }
  const post = (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setMessages(prev => [...prev, {
      id: `m-${Date.now()}`,
      author: 'Milan R.',
      initials: 'MR',
      kind: 'user',
      time: new Date().toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      }),
      text
    }]);
    setDraft('');
    setJustPosted(true);
  };
  return <section className="flex h-[420px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white" data-id="element-650" data-mp="2jys5hhnnzgxk">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-5 py-3.5" data-id="element-651" data-mp="mye6kgzh3ibi">
        <HashIcon className="h-4 w-4 text-gray-500" aria-hidden="true" data-id="element-652" data-mp="rwfm4rxowzi8" />
        <h3 className="text-[14px] font-semibold text-ink" data-id="element-653" data-mp="wwbsgxtv130y">{channel.name}</h3>
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200" data-id="element-654" data-mp="wyeqo55622gr">
          {channel.members.length} members
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-medium text-success" data-id="element-655" data-mp="15w8lfbna8o1t">
          <ZapIcon className="h-3 w-3" aria-hidden="true" data-id="element-656" data-mp="1atqnqj6so50u" />
          Synced with Salesforce
        </span>
      </header>

      <ul className="scrollbar-thin flex-1 space-y-3 overflow-y-auto px-5 py-3.5" data-id="element-657" data-mp="1kmftqyjksqos">
        {messages.map(message => <li key={message.id} className="flex gap-2.5" data-id="element-658" data-mp="1pm0sb7uu2x0z">
            <span className={['flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10.5px] font-bold', message.kind === 'salesforce' ? 'bg-navy text-white' : 'bg-brand-soft text-brand-dark'].join(' ')} data-id="element-659" data-mp="1tmkregyywapr">
              {message.initials}
            </span>
            <div className="min-w-0" data-id="element-660" data-mp="3561huua95uj6">
              <p className="flex flex-wrap items-baseline gap-2" data-id="element-661" data-mp="305dow32gr3mb">
                <span className="text-[12.5px] font-semibold text-ink" data-id="element-662" data-mp="30770lwrxoe16">
                  {message.author}
                </span>
                {message.kind === 'salesforce' && <span className="rounded bg-navy-soft px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-navy" data-id="element-663" data-mp="3svmciafrqg8a">
                    App
                  </span>}
                <span className="text-[11px] text-gray-400" data-id="element-664" data-mp="3j1lzlme8w6p0">{message.time}</span>
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-[12.5px] leading-snug text-gray-700" data-id="element-665" data-mp="kf12cqbehh3l">
                {message.text}
              </p>
            </div>
          </li>)}
        <div ref={endRef} data-id="element-666" data-mp="3f0ro62nri0" />
      </ul>

      <form onSubmit={post} className="border-t border-gray-200 px-4 py-3" data-id="element-667" data-mp="46la54ohdysp">
        <div className="flex items-end gap-2" data-id="element-668" data-mp="44h004zzo3jz">
          <label htmlFor="slack-draft" className="sr-only" data-id="element-669" data-mp="3mczqtbe4wmus">
            Post an update to #{channel.name}
          </label>
          <textarea id="slack-draft" value={draft} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            post(event);
          }
        }} rows={2} placeholder={`Post an update to #${channel.name}…`} className="min-h-[44px] flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-[12.5px] text-ink placeholder:text-gray-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" data-id="element-670" data-mp="3rbt4p4xc2fvh" />
          <button type="submit" disabled={!draft.trim()} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:bg-gray-300" data-id="element-671" data-mp="2rb7h44ykoq0o">
            <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-672" data-mp="2w1nuxq6b2dnk" />
            Post
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500" role="status" data-id="element-673" data-mp="28btllil73782">
          {justPosted ? `Posted to #${channel.name} in Slack` : `Updates post to Slack and log as activity on ${loanNumber}. Enter to send, Shift + Enter for a new line.`}
        </p>
      </form>
    </section>;
}